import TradeESIDSelect, { IESIDOption } from "@/components/UI/TradeESIDSelect";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { DRAG_HEADER } from "@/lib/reactGridLayout/dndConstants";
import { IActiveId } from "@/lib/widgetsLayoutDir";
import { logClientError } from "@/lib/errorLoggingACTIONS";
import { IImportJsonBlocks } from "@/store/importBlocksAndAllocationsStore";
import { ItradeBlockData } from "@/store/tradeBlockDataStore";
import { getBlocksData } from "@/store/tradeBlockDataStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchSwapCurvesByBlocks } from "./helper/swapCurveACTIONS";
import {
  CounterpartyQuoteMarker,
  extractCounterpartyQuotesFromTrade,
} from "./helper/counterpartyQuoteHelpers";
import {
  ONLY_ORIGINATED_TRADES_MESSAGE,
  ONLY_SWAP_BLOCKS_MESSAGE,
  ONLY_SWAP_TRADES_MESSAGE,
  SWAP_CURVE_DROPDOWN_PLACEHOLDER,
  SWAP_CURVE_EMPTY_HINT,
} from "./helper/swapCurveConstants";
import {
  curveDisplayName,
  extractIndicativeMidsFromTrade,
  extractSwapBlockIdsFromTrade,
  findBlockById,
  hasCounterpartyQuoteLegs,
  IndicativeMidMarker,
  isResponderTrade,
  isSwapBlock,
  mapSwapCurveRowsToPoints,
  SwapCurve,
  SwapCurveEntry,
} from "./helper/swapCurveHelpers";
import SwapCurveChart, { SwapCurveSeries } from "./SwapCurveChart";

interface ISwapCurveHandler {
  cryptoID?: string;
  activeId?: Partial<IActiveId>;
  tradeData: ExecutionStrategy[];
  allBlocks: IImportJsonBlocks[];
  tradeBlockData?: ItradeBlockData[];
}

const NO_TRADE_BLOCK_DATA: ItradeBlockData[] = [];

const DEFAULT_ACTIVE_ID: Partial<IActiveId> = {
  isActive: false,
  value: null,
  headerDragging: null,
  eventOver: "",
};

// Swap Curve widget body. Accepts swap trades (by ESID, via dropdown or drag from the
// Blotter) and swap blocks (via drag from Trade Creation), collecting their block ids
// into an array that will eventually be sent to the back end. See README.md.
const SwapCurveHandler: React.FC<ISwapCurveHandler> = ({
  cryptoID,
  activeId = DEFAULT_ACTIVE_ID,
  tradeData,
  allBlocks,
  tradeBlockData = NO_TRADE_BLOCK_DATA,
}) => {
  const { theme } = useTheme();
  const { getAccessTokenSilently } = useAuth0();

  const [selectedESID, setSelectedESID] = useState<string>("");
  // Only the most recent item is kept — a new drop/selection replaces the previous one.
  const [currentEntry, setCurrentEntry] = useState<SwapCurveEntry | null>(null);
  // All curves returned for the current entry's block ids (passing >1 block can return several).
  // Every returned curve is overlaid on the chart; the chart's legend toggles them on/off.
  const [curves, setCurves] = useState<SwapCurve[]>([]);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const validationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearValidationTimer = () => {
    if (validationTimer.current) {
      clearTimeout(validationTimer.current);
      validationTimer.current = null;
    }
  };

  // Show a validation message, then auto-revert to the normal view after 3 seconds.
  const showValidationMessage = (message: string) => {
    setValidationMessage(message);
    clearValidationTimer();
    validationTimer.current = setTimeout(
      () => setValidationMessage(null),
      3000,
    );
  };

  const clearValidationMessage = () => {
    clearValidationTimer();
    setValidationMessage(null);
  };

  // Clear any pending timer on unmount.
  useEffect(() => {
    return () => clearValidationTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const esidOptions: IESIDOption[] = tradeData.map((es) => ({
    label: es.esid,
    value: es.esid,
  }));
  const selectedOption =
    esidOptions.find((option) => option.value === selectedESID) ?? null;

  // ---- set / clear the current item -----------------------------------------
  // Each new drop or selection REPLACES the previous item — the widget only ever shows the
  // most recent. A new (invalid) item also clears the old one, so we never flip back to it.

  const addTradeByESID = (esid?: string | null) => {
    if (!esid) return;
    // Reflect the picked/dropped trade in the dropdown even when it has no swap legs.
    setSelectedESID(esid);
    // A trade's blocks are its legs (RP: trade.legs, OP: executionRequestCounterParties[].legs).
    const trade = tradeData.find((t) => t.esid === esid);
    // RP trades weren't originated by this user — no curve data exists, so skip the network call.
    if (isResponderTrade(trade)) {
      setCurrentEntry(null);
      showValidationMessage(ONLY_ORIGINATED_TRADES_MESSAGE);
      return;
    }
    const swapBlockIds = extractSwapBlockIdsFromTrade(trade);
    if (swapBlockIds.length === 0) {
      setCurrentEntry(null);
      showValidationMessage(ONLY_SWAP_TRADES_MESSAGE);
      return;
    }
    clearValidationMessage();
    setCurrentEntry({
      source: "Trade",
      sourceId: esid,
      label: esid,
      blockIds: swapBlockIds,
    });
  };

  const addBlockById = (blockId?: string | null) => {
    // Block drops carry the block id as their drag value (CreationSecurityCellRenderer).
    const block = findBlockById(allBlocks, blockId);
    if (!isSwapBlock(block)) {
      setCurrentEntry(null);
      showValidationMessage(ONLY_SWAP_BLOCKS_MESSAGE);
      return;
    }
    clearValidationMessage();
    setCurrentEntry({
      source: "Block",
      sourceId: String(block!.id),
      label: block!.securityName ?? `Block ${block!.id}`,
      blockIds: [block!.id],
    });
  };

  const clearCurrentEntry = () => {
    setCurrentEntry(null);
    setSelectedESID("");
    clearValidationMessage();
  };

  // ---- inputs: dropdown + drag-drop ----------------------------------------

  const handleSelectTrade = (option: IESIDOption | null) => {
    const esid = option?.value ?? "";
    if (!esid) {
      // Cleared the dropdown (X) — reset the selection, its blocks, and any
      // validation message. Must clear currentEntry too or the block ids persist.
      clearCurrentEntry();
      return;
    }
    addTradeByESID(esid);
  };

  // React to an item being dropped on THIS widget instance. activeId is shared across
  // widgets, so guard on eventOver === cryptoID (the dropped-on widget's layout id).
  useEffect(() => {
    if (!activeId || !activeId.eventOver || activeId.eventOver !== cryptoID) {
      return;
    }
    if (activeId.headerDragging === DRAG_HEADER.ESID) {
      addTradeByESID(activeId.value ?? undefined);
    } else if (activeId.headerDragging === DRAG_HEADER.BLOCK) {
      addBlockById(activeId.value ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // ---- fetch curve data for the current entry -------------------------------
  // When the entry (its block ids) changes, call the back end and keep all returned curves. The
  // `cancelled` guard drops a stale response if the entry changes again before this resolves. On
  // error we clear the curves so the chart simply shows nothing.
  useEffect(() => {
    if (!currentEntry || currentEntry.blockIds.length === 0) {
      setCurves([]);
      return;
    }

    let cancelled = false;
    setCurves([]);

    fetchSwapCurvesByBlocks(currentEntry.blockIds, getAccessTokenSilently)
      .then((result) => {
        if (cancelled) return;
        setCurves(result ?? []);
      })
      .catch((error) => {
        logClientError(
          "SwapCurveHandler.fetchSwapCurvesByBlocks",
          error,
          getAccessTokenSilently,
        );
        // fetchRequestWrapper already surfaces the error; render an empty chart.
        if (!cancelled) setCurves([]);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntry]);

  // Every returned curve mapped to an overlaid series (name + plotted points). When more than
  // one comes back they are drawn together and the chart legend toggles them.
  const curveSeriesList = useMemo<SwapCurveSeries[]>(
    () =>
      curves.map((curve, index) => ({
        name: curveDisplayName(curve, index),
        points: mapSwapCurveRowsToPoints(curve.dataPoints ?? []),
      })),
    [curves],
  );

  // ---- assigned blocks for a trade with no CP legs (status New, LQC-2908) ---
  // Blocks only load when a blotter row is expanded, so fetch them here when they're missing.
  // The store entry exists (even empty) once fetched, so this never repeats for the same ESID.
  useEffect(() => {
    if (!currentEntry || currentEntry.source !== "Trade") return;
    const esid = currentEntry.sourceId;
    const trade = tradeData.find((t) => t.esid === esid);
    if (!trade || isResponderTrade(trade) || hasCounterpartyQuoteLegs(trade)) return;
    if (tradeBlockData.some((group) => group.esid === esid)) return;

    getBlocksData({ esid, getAccessTokenSilently }).catch((error) =>
      logClientError("SwapCurveHandler.getBlocksData", error, getAccessTokenSilently),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntry]);

  // OP-only: indicative mids per swap block (from the first CP, or the assigned blocks at New),
  // overlaid as chart markers. Empty for block drops, RP trades, or when no mid is numeric.
  const indicativeMidMarkers = useMemo<IndicativeMidMarker[]>(() => {
    if (!currentEntry || currentEntry.source !== "Trade") return [];
    const trade = tradeData.find((t) => t.esid === currentEntry.sourceId);
    const assignedBlocks = tradeBlockData.find(
      (group) => group.esid === currentEntry.sourceId,
    )?.blocks;
    return extractIndicativeMidsFromTrade(
      trade,
      currentEntry.blockIds,
      assignedBlocks,
    );
  }, [currentEntry, tradeData, tradeBlockData]);

  // OP-only: every live counterparty quote on the swap blocks (LQC-1899). All quoting CPs are
  // shown; the chart draws the leading one last so it stays visible where dots overlap.
  const counterpartyQuotes = useMemo<CounterpartyQuoteMarker[]>(() => {
    if (!currentEntry || currentEntry.source !== "Trade") return [];
    const trade = tradeData.find((t) => t.esid === currentEntry.sourceId);
    return extractCounterpartyQuotesFromTrade(trade, currentEntry.blockIds);
  }, [currentEntry, tradeData]);

  // ---- render helpers -------------------------------------------------------

  const renderBody = () => {
    if (validationMessage) {
      return (
        <div
          data-testid="SwapCurveValidationMessage"
          id="SwapCurveValidationMessage"
          className="flex h-full w-full items-center justify-center px-4 text-center text-sm font-medium text-red-600 dark:text-red-400"
        >
          {validationMessage}
        </div>
      );
    }

    if (!currentEntry) {
      return (
        <div
          data-testid="SwapCurveEmptyHint"
          id="SwapCurveEmptyHint"
          className="flex h-full w-full items-center justify-center px-4 text-center text-sm text-gray-400 dark:text-zinc-500"
        >
          {SWAP_CURVE_EMPTY_HINT}
        </div>
      );
    }

    // Once an item is selected we have its swap block id(s), so show the curve(s). The trade
    // itself isn't displayed here; the block ids live in the header (top-right). Every returned
    // curve is overlaid and the chart's legend lets the user toggle individual lines.
    return (
      <div
        data-testid="SwapCurveCurrentEntry"
        id="SwapCurveCurrentEntry"
        className="flex h-full w-full flex-col p-2"
      >
        <div className="min-h-0 w-full flex-1">
          <SwapCurveChart
            curves={curveSeriesList}
            indicativeMids={indicativeMidMarkers}
            counterpartyQuotes={counterpartyQuotes}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="SwapCurveContainer"
      id="SwapCurveContainer"
      className="flex h-full w-full flex-col bg-cubeBase-500 pt-1 dark:bg-cubeBaseDark-500"
    >
      <div className="flex-0 flex h-fit w-full items-center gap-2 pt-1">
        <div className="min-w-0 flex-1">
          <TradeESIDSelect
            options={esidOptions}
            value={selectedOption}
            onChange={handleSelectTrade}
            theme={theme}
            placeholder={SWAP_CURVE_DROPDOWN_PLACEHOLDER}
          />
        </div>
        {currentEntry && currentEntry.blockIds.length > 0 && (
          <div className="flex flex-shrink-0 items-center gap-1 pr-1">
            <span
              data-testid="SwapCurveEntryBlockIds"
              id="SwapCurveEntryBlockIds"
              className="whitespace-nowrap text-[11px] text-gray-500 dark:text-zinc-400"
            >
              Block ID{currentEntry.blockIds.length === 1 ? "" : "s"}:{" "}
              {currentEntry.blockIds.join(", ")}
            </span>
            <button
              type="button"
              data-testid="SwapCurveRemoveButton"
              id="SwapCurveRemoveButton"
              className="hide-button-fromDrag text-gray-400 hover:text-red-500"
              onClick={clearCurrentEntry}
            >
              ✕
            </button>
          </div>
        )}
      </div>
      <div className="min-h-0 w-full flex-1 py-2">{renderBody()}</div>
    </div>
  );
};

export default SwapCurveHandler;
